'use client';

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { useAuthUser } from '@/hooks/useAuthUser';
import { useTranslations } from 'next-intl';
import { type ReactNode, useEffect, useState } from 'react';
import { UserAvatar } from './UserAvatar';
import { UserMenuContent } from './UserMenuContent';

interface ThemedUserButtonProps {
  userProfileUrl?: '/user-profile';
  /** Custom trigger element. When provided, replaces the default avatar button. */
  children?: ReactNode;
}

/**
 * User button with dropdown menu.
 * Shows avatar trigger (or custom children) that opens a menu with profile link and sign-out.
 *
 * Usage:
 *   <ThemedUserButton />                           — default avatar trigger
 *   <ThemedUserButton><MyCustomButton /></ThemedUserButton>  — custom trigger
 */
export function ThemedUserButton({
  userProfileUrl = '/user-profile',
  children,
}: ThemedUserButtonProps) {
  const [mounted, setMounted] = useState(false);
  const { user, userEmail } = useAuthUser();
  const t = useTranslations('UserMenu');

  useEffect(() => {
    setMounted(true);
  }, []);

  if (!mounted) return null;

  const displayName = user?.fullName || userEmail || t('defaultName');

  const defaultTrigger = (
    <button
      type='button'
      className='flex items-center justify-center rounded-full hover:opacity-90 transition-opacity focus:outline-none focus:ring-2 focus:ring-brand-tertiary focus:ring-offset-2'
      aria-label={displayName}
    >
      <UserAvatar displayName={displayName} size='md' />
    </button>
  );

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        {children ?? defaultTrigger}
      </DropdownMenuTrigger>
      <DropdownMenuContent align='end' className='w-64'>
        <UserMenuContent userProfileUrl={userProfileUrl} />
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
